import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthInit } from '@/hooks/useAuth'
import { useSessionPersistence } from '@/hooks/useSessionPersistence'
import { ROUTES } from '@/config/routes'

// ── Auth ────────────────────────────────────────────────────────────────────
import Login        from '@/pages/auth/Login'
import AuthCallback from '@/pages/auth/AuthCallback'
import SetupProfile from '@/pages/auth/SetupProfile'

// ── Layout / Guards ─────────────────────────────────────────────────────────
import ProtectedRoute from '@/components/layout/ProtectedRoute'

// ── Admin pages ─────────────────────────────────────────────────────────────
import AdminDashboard  from '@/pages/admin/AdminDashboard'
import AdminProposals  from '@/pages/admin/AdminProposals'
import AdminSiteVisits from '@/pages/admin/AdminSiteVisits'
import AdminPayments   from '@/pages/admin/AdminPayments'
import AdminWorkers    from '@/pages/admin/AdminWorkers'
import AdminCustomers  from '@/pages/admin/AdminCustomers'

// ── Customer pages ───────────────────────────────────────────────────────────
import BookingEntry       from '@/pages/customer/BookingEntry'
import DescribeProblem    from '@/pages/customer/DescribeProblem'
import ViewQuote          from '@/pages/customer/ViewQuote'
import FinalProposalRequest  from '@/pages/customer/FinalProposalRequest'
import MyProjects         from '@/pages/customer/MyProjects'
import BookingConfirmed   from '@/pages/customer/BookingConfirmed'
import TrackBooking       from '@/pages/customer/TrackBooking'
import ScheduleSiteVisit  from '@/pages/customer/ScheduleSiteVisit'
import UploadPayment      from '@/pages/customer/UploadPayment'
import SignContract       from '@/pages/customer/SignContract'
import JobCompletion      from '@/pages/customer/JobCompletion'
import ReviewWorker       from '@/pages/customer/ReviewWorker'

// ── Worker pages ─────────────────────────────────────────────────────────────
import WorkerDashboard from '@/pages/worker/WorkerDashboard'
import JobDetails      from '@/pages/worker/JobDetails'
import OnSite          from '@/pages/worker/OnSite'
import CompleteJob     from '@/pages/worker/CompleteJob'
import WorkerEarnings  from '@/pages/worker/WorkerEarnings'

// ── Shared pages ─────────────────────────────────────────────────────────────
import UserProfile from '@/pages/UserProfile'

// ── Public pages ─────────────────────────────────────────────────────────────
import LandingPage from '@/pages/LandingPage'

// ── Placeholder (unbuilt pages) ─────────────────────────────────────────────
import PlaceholderPage from '@/pages/PlaceholderPage'

/**
 * Router — define all routes here. Unbuilt routes use PlaceholderPage so
 * the full navigation is testable from day one.
 *
 * ProtectedRoute usage:
 *   <Route element={<ProtectedRoute />}>              — any authenticated user
 *   <Route element={<ProtectedRoute requiredRole="worker" />}> — workers only
 *   <Route element={<ProtectedRoute requiredRole="admin"  />}> — admins only
 */
function Router() {
  // One-time: subscribe to Supabase auth state changes
  useAuthInit()
  // Prevent false logouts on tab switch / app minimize
  useSessionPersistence()

  return (
    <Routes>
      {/* ── Public ─────────────────────────────────────────────────────────── */}
      <Route path={ROUTES.LOGIN}         element={<Login />} />
      <Route path={ROUTES.AUTH_CALLBACK} element={<AuthCallback />} />
      <Route path="/setup-profile"       element={<SetupProfile />} />

      {/* ── Customer (any authenticated user with role=customer) ─────────── */}
      <Route element={<ProtectedRoute requiredRole="customer" />}>
        <Route path={ROUTES.BOOK}          element={<BookingEntry />} />
        <Route path={ROUTES.BOOK_CATEGORY} element={<DescribeProblem />} />
        <Route path={ROUTES.BOOK_QUOTE}    element={<ViewQuote />} />
        <Route path={ROUTES.BOOK_SCHEDULE}       element={<PlaceholderPage pageName="Select Schedule" />} />
        <Route path={ROUTES.BOOK_FINAL_PROPOSAL} element={<FinalProposalRequest />} />
        <Route path={ROUTES.BOOK_CONTRACT} element={<SignContract />} />
        <Route path={ROUTES.BOOK_PAYMENT}  element={<UploadPayment />} />
        <Route path={ROUTES.BOOK_CONFIRMED}element={<BookingConfirmed />} />
        <Route path={ROUTES.BOOK_TRACK}    element={<TrackBooking />} />
        <Route path={ROUTES.BOOK_PROGRESS} element={<PlaceholderPage pageName="Job in Progress" />} />
        <Route path={ROUTES.BOOK_COMPLETE} element={<JobCompletion />} />
        <Route path={ROUTES.BOOK_REVIEW}   element={<ReviewWorker />} />
        <Route path={ROUTES.BOOK_HISTORY}    element={<MyProjects />} />
        <Route path={ROUTES.BOOK_SITE_VISIT} element={<ScheduleSiteVisit />} />
        <Route path="/profile"             element={<UserProfile />} />
      </Route>

      {/* ── Worker ───────────────────────────────────────────────────────── */}
      <Route element={<ProtectedRoute requiredRole="worker" />}>
        <Route path={ROUTES.WORKER_HOME}       element={<WorkerDashboard />} />
        <Route path={ROUTES.WORKER_JOB}        element={<JobDetails />} />
        <Route path={ROUTES.WORKER_PREPARE}    element={<PlaceholderPage pageName="Pre-Dispatch Checklist" />} />
        <Route path={ROUTES.WORKER_ONSITE}     element={<OnSite />} />
        <Route path={ROUTES.WORKER_ADDITIONAL} element={<PlaceholderPage pageName="Additional Work Request" />} />
        <Route path={ROUTES.WORKER_COMPLETE}   element={<CompleteJob />} />
        <Route path={ROUTES.WORKER_EARNINGS}   element={<WorkerEarnings />} />
        <Route path="/profile"                 element={<UserProfile />} />
      </Route>

      {/* ── Admin ────────────────────────────────────────────────────────── */}
      <Route element={<ProtectedRoute requiredRole="admin" />}>
        <Route path={ROUTES.ADMIN}            element={<AdminDashboard />} />
        <Route path={ROUTES.ADMIN_QUOTES}       element={<AdminProposals />} />
        <Route path={ROUTES.ADMIN_SITE_VISITS}  element={<AdminSiteVisits />} />
        <Route path={ROUTES.ADMIN_WORKERS}    element={<AdminWorkers />} />
        <Route path={ROUTES.ADMIN_RATES}      element={<PlaceholderPage pageName="Rate Table" />} />
        <Route path={ROUTES.ADMIN_FINANCIALS} element={<AdminPayments />} />
        <Route path={ROUTES.ADMIN_CUSTOMERS}  element={<AdminCustomers />} />
      </Route>

      {/* ── Landing page ─────────────────────────────────────────────────── */}
      <Route path="/" element={<LandingPage />} />

      {/* ── 404 ──────────────────────────────────────────────────────────── */}
      <Route path="*" element={<PlaceholderPage pageName="Page not found" />} />
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <Router />
    </BrowserRouter>
  )
}
